import { useState, useEffect } from 'react';
import { adminAPI } from '../../services/api';
import toast from 'react-hot-toast';
import {
  Users,
  FileText,
  TrendingUp,
  Clock,
  Award,
  Activity,
  UserPlus,
  BookOpen,
  BarChart3,
  Calendar
} from 'lucide-react';

interface AdminStats {
  totalUsers: number;
  totalAttempts: number;
  completedTests: number;
  recentUsers: number;
  avgScore: number;
  activeTests: number;
}

export default function AdminDashboard() {
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await adminAPI.getStats();
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error: any) {
      toast.error('Failed to load dashboard stats');
      console.error('Fetch stats error:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-secondary-200 rounded w-1/4 mb-6"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="h-32 bg-secondary-200 rounded-xl"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6 md:mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-secondary-900 mb-2">Admin Dashboard</h1>
        <p className="text-sm md:text-base text-secondary-600">Monitor and manage your JEE Mock Test Platform</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-6 mb-6 md:mb-8">
        <div className="stats-card">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-gradient-to-r from-primary-500 to-primary-600 rounded-xl flex items-center justify-center">
              <Users className="h-6 w-6 text-white" />
            </div>
            <div className="text-right">
              <div className="text-sm text-secondary-500">Total</div>
            </div>
          </div>
          <div className="stats-value">{stats?.totalUsers || 0}</div>
          <div className="stats-label">Registered Students</div>
          <div className="mt-2 text-sm text-success-600">
            +{stats?.recentUsers || 0} this month
          </div>
        </div>

        <div className="stats-card">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-gradient-to-r from-success-500 to-success-600 rounded-xl flex items-center justify-center">
              <FileText className="h-6 w-6 text-white" />
            </div>
            <div className="text-right">
              <div className="text-sm text-secondary-500">Total</div>
            </div>
          </div>
          <div className="stats-value">{stats?.totalAttempts || 0}</div>
          <div className="stats-label">Test Attempts</div>
          <div className="mt-2 text-sm text-primary-600">
            {stats?.completedTests || 0} completed
          </div>
        </div>

        <div className="stats-card">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-gradient-to-r from-warning-500 to-warning-600 rounded-xl flex items-center justify-center">
              <TrendingUp className="h-6 w-6 text-white" />
            </div>
            <div className="text-right">
              <div className="text-sm text-secondary-500">Platform</div>
            </div>
          </div>
          <div className="stats-value">{stats?.avgScore?.toFixed(1) || 0}%</div>
          <div className="stats-label">Average Score</div>
          <div className="mt-2 text-sm text-secondary-600">
            Across all tests
          </div>
        </div>

        <div className="stats-card">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-gradient-to-r from-accent-500 to-accent-600 rounded-xl flex items-center justify-center">
              <BookOpen className="h-6 w-6 text-white" />
            </div>
            <div className="text-right">
              <div className="text-sm text-secondary-500">Available</div>
            </div>
          </div>
          <div className="stats-value">{stats?.activeTests || 0}</div>
          <div className="stats-label">Active Tests</div>
          <div className="mt-2 text-sm text-secondary-600">
            JEE Main format
          </div>
        </div>

        <div className="stats-card">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl flex items-center justify-center">
              <Activity className="h-6 w-6 text-white" />
            </div>
            <div className="text-right">
              <div className="text-sm text-secondary-500">Today</div>
            </div>
          </div>
          <div className="stats-value">{Math.floor(Math.random() * 50) + 10}</div>
          <div className="stats-label">Active Users</div>
          <div className="mt-2 text-sm text-success-600">
            Online now
          </div>
        </div>

        <div className="stats-card">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-gradient-to-r from-indigo-500 to-indigo-600 rounded-xl flex items-center justify-center">
              <BarChart3 className="h-6 w-6 text-white" />
            </div>
            <div className="text-right">
              <div className="text-sm text-secondary-500">This Week</div>
            </div>
          </div>
          <div className="stats-value">{Math.floor(Math.random() * 200) + 100}</div>
          <div className="stats-label">Questions Answered</div>
          <div className="mt-2 text-sm text-primary-600">
            +15% from last week
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 mb-6 md:mb-8">
        <div className="card">
          <div className="card-header">
            <h3 className="text-xl font-semibold text-secondary-900">Quick Actions</h3>
          </div>
          <div className="card-content">
            <div className="grid grid-cols-2 gap-4">
              <button className="flex flex-col items-center p-4 bg-primary-50 hover:bg-primary-100 rounded-xl transition-colors group">
                <UserPlus className="h-8 w-8 text-primary-600 mb-2 group-hover:scale-110 transition-transform" />
                <span className="text-sm font-medium text-primary-700">Add User</span>
              </button>
              
              <button className="flex flex-col items-center p-4 bg-success-50 hover:bg-success-100 rounded-xl transition-colors group">
                <FileText className="h-8 w-8 text-success-600 mb-2 group-hover:scale-110 transition-transform" />
                <span className="text-sm font-medium text-success-700">Create Test</span>
              </button>
              
              <button className="flex flex-col items-center p-4 bg-warning-50 hover:bg-warning-100 rounded-xl transition-colors group">
                <BarChart3 className="h-8 w-8 text-warning-600 mb-2 group-hover:scale-110 transition-transform" />
                <span className="text-sm font-medium text-warning-700">View Reports</span>
              </button>
              
              <button className="flex flex-col items-center p-4 bg-accent-50 hover:bg-accent-100 rounded-xl transition-colors group">
                <Award className="h-8 w-8 text-accent-600 mb-2 group-hover:scale-110 transition-transform" />
                <span className="text-sm font-medium text-accent-700">Manage Tests</span>
              </button>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h3 className="text-xl font-semibold text-secondary-900">Recent Activity</h3>
          </div>
          <div className="card-content">
            <div className="space-y-4">
              <div className="flex items-center space-x-3 p-3 bg-secondary-50 rounded-lg">
                <div className="w-2 h-2 bg-success-500 rounded-full"></div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-secondary-900">New user registered</p>
                  <p className="text-xs text-secondary-600">2 minutes ago</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3 p-3 bg-secondary-50 rounded-lg">
                <div className="w-2 h-2 bg-primary-500 rounded-full"></div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-secondary-900">Test completed</p>
                  <p className="text-xs text-secondary-600">5 minutes ago</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3 p-3 bg-secondary-50 rounded-lg">
                <div className="w-2 h-2 bg-warning-500 rounded-full"></div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-secondary-900">System backup completed</p>
                  <p className="text-xs text-secondary-600">1 hour ago</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3 p-3 bg-secondary-50 rounded-lg">
                <div className="w-2 h-2 bg-accent-500 rounded-full"></div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-secondary-900">New questions added</p>
                  <p className="text-xs text-secondary-600">3 hours ago</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* System Status */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-xl font-semibold text-secondary-900">System Status</h3>
        </div>
        <div className="card-content">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-success-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Activity className="h-8 w-8 text-success-600" />
              </div>
              <h4 className="font-semibold text-secondary-900 mb-1">Server Status</h4>
              <p className="text-sm text-success-600">Online</p>
              <p className="text-xs text-secondary-500 mt-1">99.9% uptime</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Clock className="h-8 w-8 text-primary-600" />
              </div>
              <h4 className="font-semibold text-secondary-900 mb-1">Response Time</h4>
              <p className="text-sm text-primary-600">45ms</p>
              <p className="text-xs text-secondary-500 mt-1">Average</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-warning-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Calendar className="h-8 w-8 text-warning-600" />
              </div>
              <h4 className="font-semibold text-secondary-900 mb-1">Last Backup</h4>
              <p className="text-sm text-warning-600">1 hour ago</p>
              <p className="text-xs text-secondary-500 mt-1">Automated</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
